import React from 'react';

import { Form } from 'antd';
import type { Rule } from 'antd/es/form';
import styled from 'styled-components';

import { FeeType } from 'types/fees';
import { normalizeValue } from 'utils/helpers/form';

import CloseIcon from '../../../common/Icons/CloseIcon';
import InputNumber from '../../lib/InputNumber';
import { FEES_FIELDS, FEES_LEVELS_FIELDS, FIELD_LABELS, LEVEL_FEES_FIELDS, RESPITE_FEE_MAX_VALUE } from '../constants';

const StyledCloseIcon = styled.div`
  display: flex;
  visibility: hidden;
  border-radius: 50%;
  cursor: pointer;
  align-items: center;
  margin-top: 11px;
`;

const Container = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  &:hover {
    ${StyledCloseIcon} {
      visibility: visible;
    }
  }
`;

const Label = styled.div`
  display: flex;
  margin-right: 5px;
  margin-top: 11px;
  min-width: 200px;

  justify-content: end;
  font-family: var(--font-bold);
  font-size: 14px;
`;

interface Props {
  category: string;
  field: string;
  name?: (string | number)[];
  required?: boolean;
  disabled?: boolean;
  onRemove?: (field: string) => void;
  dataTestId?: string;
  rules?: Rule[];
}

const ShopDataModalFeesEntryNumeric = ({
  category,
  field,
  name,
  required,
  disabled,
  onRemove,
  dataTestId,
  rules,
}: Props) => {
  const enabledRemoval = typeof onRemove === 'function';
  const isRespiteFee = FEES_FIELDS.RESPITE_FEE === field;
  const [, type] = name ?? [];
  const isAverageCost = field === LEVEL_FEES_FIELDS.AVERAGE_COST;
  const isLevelFee = type === FeeType.CARE_FEE && (Object.values(FEES_LEVELS_FIELDS).includes(field) || (isAverageCost && required));

  const defaultRules: Rule[] = [
    { required, message: isAverageCost ? 'Average care fee is required' : '' },
    {
      validator: (_, value) => {
        if (isRespiteFee && Number.isFinite(value) && (value < 0 || value > RESPITE_FEE_MAX_VALUE)) {
          return Promise.reject('Value must be between 0 and 1000');
        }
        if(value < 0){
          return Promise.reject('');
        }
        return Promise.resolve();
      },
    },
  ];
  const mergedRules = [...defaultRules, ...(rules ?? [])];

  return (
    <Container>
      <Label>{FIELD_LABELS[category][field]}</Label>
      <Form.Item name={name ?? [category, field]} rules={mergedRules} normalize={normalizeValue()}>
        <InputNumber disabled={disabled} style={{ width: 220, height: 36 }} prefix={'$'} data-test-id={dataTestId} />
      </Form.Item>
      {enabledRemoval && (
        <StyledCloseIcon onClick={() => onRemove(field)}>
          <CloseIcon size={20} />
        </StyledCloseIcon>
      )}
    </Container>
  );
};

export default ShopDataModalFeesEntryNumeric;
